<template>
    <teleport to="body">
        <transition leave-active-class="duration-200">
            <div v-show="show" v-bind="$attrs" class="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50" scroll-region role="dialog" aria-modal="true">
                <transition enter-active-class="ease-out duration-300"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="ease-in duration-200"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0">
                    <div v-show="show" class="fixed inset-0 transform transition-all" @click="close">
                        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                    </div>
                </transition>

                <transition enter-active-class="ease-out duration-300"
                        enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                        leave-active-class="ease-in duration-200"
                        leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                        leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div ref="dialog" v-show="show" class="mb-6 bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:w-full sm:mx-auto" :class="maxWidthClass">
                        <slot v-if="show"></slot>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>

<script>
import { onMounted, onUnmounted, ref, watch, nextTick } from "vue";

export default {
        emits: ['close'],

        props: {
            show: {
                default: false
            },
            maxWidth: {
                default: '2xl'
            },
            closeable: {
                default: true
            },
        },

        setup(props, {emit}) {
            const dialog = ref(null)
            let previousFocus = null

            const close = () => {
                if (props.closeable) {
                    emit('close')
                }
            }

            const closeOnEscape = (e) => {
                if (e.key === 'Escape' && props.show) {
                    close()
                }
            }

            const getFocusableElements = () => {
                if (!dialog.value) return []
                const focusable = dialog.value.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), details, [tabindex]:not([tabindex="-1"])')
                return Array.from(focusable).filter(el => !el.closest('[aria-hidden="true"]'))
            }

            const handleFocusIn = (e) => {
                if (!props.show || !dialog.value) return

                // If focus is outside our dialog
                if (!dialog.value.contains(e.target)) {
                    // If there are other modals open, we should only trap focus if we are the topmost one.
                    // Since modals are teleported to body, the last one in the DOM is usually the topmost.
                    const allModals = document.querySelectorAll('[role="dialog"]')
                    if (allModals.length > 0 && allModals[allModals.length - 1] !== dialog.value.closest('[role="dialog"]')) {
                        return
                    }

                    const focusableElements = getFocusableElements()
                    if (focusableElements.length > 0) {
                        focusableElements[0].focus()
                    }
                }
            }

            const focusTrap = (e) => {
                if (e.key === 'Tab') {
                    const focusableElements = getFocusableElements()
                    if (focusableElements.length === 0) {
                        e.preventDefault()
                        return
                    }

                    const firstFocusableElement = focusableElements[0]
                    const lastFocusableElement = focusableElements[focusableElements.length - 1]

                    if (e.shiftKey) {
                        if (document.activeElement === firstFocusableElement) {
                            e.preventDefault()
                            lastFocusableElement?.focus()
                        }
                    } else {
                        if (document.activeElement === lastFocusableElement) {
                            e.preventDefault()
                            firstFocusableElement?.focus()
                        }
                    }
                }
            }

            watch(() => props.show, (show) => {
                if (show) {
                    document.body.style.overflow = 'hidden'
                    previousFocus = document.activeElement
                    nextTick(() => {
                        const focusableElements = getFocusableElements()
                        if (focusableElements.length > 0) {
                            focusableElements[0].focus()
                        }
                        document.addEventListener('keydown', focusTrap)
                        document.addEventListener('focusin', handleFocusIn)
                    })
                } else {
                    document.body.style.overflow = null
                    document.removeEventListener('keydown', focusTrap)
                    document.removeEventListener('focusin', handleFocusIn)
                    if (previousFocus) {
                        previousFocus.focus()
                    }
                }
            }, { immediate: true })

            onMounted(() => document.addEventListener('keydown', closeOnEscape))
            onUnmounted(() => {
                document.removeEventListener('keydown', closeOnEscape)
                document.body.style.overflow = null
                document.removeEventListener('keydown', focusTrap)
                document.removeEventListener('focusin', handleFocusIn)
            })

            return {
                close,
                dialog,
            }
        },

        computed: {
            maxWidthClass() {
                return {
                    'sm': 'sm:max-w-sm',
                    'md': 'sm:max-w-md',
                    'lg': 'sm:max-w-lg',
                    'xl': 'sm:max-w-xl',
                    '2xl': 'sm:max-w-2xl',
                    '3xl': 'sm:max-w-3xl',
                    '4/5': 'sm:max-w-[80%]',
                }[this.maxWidth]
            }
        }
    }
</script>
